/**
 * ServerActionsToolbar — Boutons d'actions contextuels selon status.
 *
 * Matrice status × action (cf spec §6.6) :
 *
 *   | status        | Retry | Rebuild | Suspend | Resume | Destroy | FullLogs | ForceCancel |
 *   | provisioning  |   —   |   —     |   —     |   —    |   —     |    —     |     ✅       |
 *   | installing    |   —   |   —     |   —     |   —    |   —     |    ✅    |     ✅       |
 *   | active        |   —   |   ✅    |   ✅    |   —    |   ✅    |    ✅    |     —        |
 *   | failed        |  ✅   |   ✅    |   —     |   —    |   ✅    |    ✅    |     —        |
 *   | suspended     |   —   |   —     |   —     |   ✅   |   ✅    |    ✅    |     —        |
 *   | destroyed     |   —   |   —     |   —     |   —    |   —     |    ✅    |     —        |
 *
 * ForceCancel : TODO backend → bouton rendu désactivé avec tooltip "À venir".
 *
 * @spec specs/app/admin/dedicated-servers.md §6.6
 */

import { Button, Dropdown, Input, Modal, Tooltip } from 'antd';
import {
  MoreHorizontal,
  Pause,
  Play,
  RotateCcw,
  ScrollText,
  Trash2,
  Wrench,
  XOctagon,
} from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import type { DedicatedServer, ServerStatus } from '@/types/dedicatedServer';
import ServerRetryModal from './ServerRetryModal';
import ServerRebuildModal from './ServerRebuildModal';
import ServerDestroyModal from './ServerDestroyModal';
import { useDedicatedServerActions } from '../hooks/useDedicatedServerActions';

interface ServerActionsToolbarProps {
  server: DedicatedServer;
  onFullLogsClick: () => void;
  onActionSuccess?: () => void;
}

interface ActionVisibility {
  retry: boolean;
  rebuild: boolean;
  suspend: boolean;
  resume: boolean;
  destroy: boolean;
  fullLogs: boolean;
  forceCancel: boolean;
}

const VISIBILITY_MATRIX: Record<ServerStatus, ActionVisibility> = {
  provisioning: {
    retry: false,
    rebuild: false,
    suspend: false,
    resume: false,
    destroy: false,
    fullLogs: false,
    forceCancel: true,
  },
  installing: {
    retry: false,
    rebuild: false,
    suspend: false,
    resume: false,
    destroy: false,
    fullLogs: true,
    forceCancel: true,
  },
  active: {
    retry: false,
    rebuild: true,
    suspend: true,
    resume: false,
    destroy: true,
    fullLogs: true,
    forceCancel: false,
  },
  failed: {
    retry: true,
    rebuild: true,
    suspend: false,
    resume: false,
    destroy: true,
    fullLogs: true,
    forceCancel: false,
  },
  suspended: {
    retry: false,
    rebuild: false,
    suspend: false,
    resume: true,
    destroy: true,
    fullLogs: true,
    forceCancel: false,
  },
  destroyed: {
    retry: false,
    rebuild: false,
    suspend: false,
    resume: false,
    destroy: false,
    fullLogs: true,
    forceCancel: false,
  },
};

const MIN_REASON = 10;

/** Petite modale inline pour suspend / resume (reason simple obligatoire). */
const simpleReasonModal = (
  title: string,
  description: string,
  okText: string,
  danger: boolean,
  onSubmit: (reason: string) => void,
): void => {
  let reasonValue = '';
  const modal = Modal.confirm({
    title,
    icon: null,
    okText,
    cancelText: 'Annuler',
    okButtonProps: { danger, disabled: true },
    content: (
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">{description}</p>
        <Input.TextArea
          placeholder={`Raison (min. ${MIN_REASON} caractères)`}
          rows={3}
          maxLength={500}
          showCount
          onChange={(e) => {
            reasonValue = e.target.value;
            modal.update({
              okButtonProps: {
                danger,
                disabled: reasonValue.trim().length < MIN_REASON,
              },
            });
          }}
        />
      </div>
    ),
    onOk: () => {
      if (reasonValue.trim().length < MIN_REASON) {
        return Promise.reject(new Error('Raison trop courte'));
      }
      onSubmit(reasonValue.trim());
      return Promise.resolve();
    },
  });
};

const ServerActionsToolbar: FC<ServerActionsToolbarProps> = ({
  server,
  onFullLogsClick,
  onActionSuccess,
}) => {
  const visibility = useMemo(() => VISIBILITY_MATRIX[server.status], [server.status]);
  const { suspend, resume, isSuspending, isResuming } = useDedicatedServerActions(server.id);

  const [retryOpen, setRetryOpen] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [destroyOpen, setDestroyOpen] = useState(false);

  const handleSuspend = () =>
    simpleReasonModal(
      'Suspendre le serveur',
      `La VM ${server.name} sera stoppée. Les données sont conservées, les workspaces deviennent indisponibles.`,
      'Suspendre',
      false,
      (reason) => suspend(reason, { onSuccess: onActionSuccess }),
    );

  const handleResume = () =>
    simpleReasonModal(
      'Reprendre le serveur',
      `La VM ${server.name} va être redémarrée (estimé 1–2 min).`,
      'Reprendre',
      false,
      (reason) => resume(reason, { onSuccess: onActionSuccess }),
    );

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {visibility.retry && (
          <Button
            type="primary"
            icon={<RotateCcw className="h-4 w-4" />}
            onClick={() => setRetryOpen(true)}
          >
            Relancer setup
          </Button>
        )}
        {visibility.rebuild && (
          <Button icon={<Wrench className="h-4 w-4" />} onClick={() => setRebuildOpen(true)}>
            Reconstruire
          </Button>
        )}
        {visibility.suspend && (
          <Button
            icon={<Pause className="h-4 w-4" />}
            onClick={handleSuspend}
            loading={isSuspending}
          >
            Suspendre
          </Button>
        )}
        {visibility.resume && (
          <Button
            type="primary"
            icon={<Play className="h-4 w-4" />}
            onClick={handleResume}
            loading={isResuming}
          >
            Reprendre
          </Button>
        )}
        {visibility.destroy && (
          <Button danger icon={<Trash2 className="h-4 w-4" />} onClick={() => setDestroyOpen(true)}>
            Détruire
          </Button>
        )}
        {visibility.fullLogs && (
          <Button icon={<ScrollText className="h-4 w-4" />} onClick={onFullLogsClick}>
            Voir tous les logs
          </Button>
        )}
        {visibility.forceCancel && (
          <Tooltip title="Force cancel — endpoint backend à venir">
            <Button icon={<XOctagon className="h-4 w-4" />} disabled>
              Force cancel
            </Button>
          </Tooltip>
        )}
        {/* Menu overflow pour futures actions secondaires (copier ID, console Hetzner…) */}
        <Dropdown
          menu={{
            items: [
              {
                key: 'copy-id',
                label: 'Copier ID serveur',
                onClick: () => {
                  if (typeof navigator !== 'undefined' && navigator.clipboard) {
                    void navigator.clipboard.writeText(server.id);
                  }
                },
              },
              server.hetzner_server_id
                ? {
                    key: 'hetzner-console',
                    label: 'Voir sur Hetzner Console',
                    onClick: () => {
                      window.open(
                        `https://console.hetzner.cloud/projects/all/servers/${server.hetzner_server_id}`,
                        '_blank',
                        'noopener',
                      );
                    },
                  }
                : null,
              server.stripe_subscription_id
                ? {
                    key: 'stripe',
                    label: 'Voir souscription Stripe',
                    onClick: () => {
                      window.open(
                        `https://dashboard.stripe.com/subscriptions/${server.stripe_subscription_id}`,
                        '_blank',
                        'noopener',
                      );
                    },
                  }
                : null,
            ].filter(Boolean) as { key: string; label: string; onClick: () => void }[],
          }}
        >
          <Button icon={<MoreHorizontal className="h-4 w-4" />} />
        </Dropdown>
      </div>

      <ServerRetryModal
        server={server}
        open={retryOpen}
        onClose={() => setRetryOpen(false)}
        onSuccess={onActionSuccess}
      />
      <ServerRebuildModal
        server={server}
        open={rebuildOpen}
        onClose={() => setRebuildOpen(false)}
        onSuccess={onActionSuccess}
      />
      <ServerDestroyModal
        server={server}
        open={destroyOpen}
        onClose={() => setDestroyOpen(false)}
        onSuccess={onActionSuccess}
      />
    </>
  );
};

export default ServerActionsToolbar;
